'use client';

import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';

import { useConfigValue } from '@/components/providers/config-provider';

import type { HitlGetTrailConfig, HitlModule, HitlToolkitModule } from './types';

const EMPTY_MODULE: HitlModule = {
  toolkit: {},
  humanToolNames: new Set(),
};

const HitlContext = createContext<HitlModule>(EMPTY_MODULE);

const moduleCache = new Map<number, Promise<HitlToolkitModule | null>>();
const resolvedCache = new Map<number, HitlToolkitModule | null>();

function loadHitlToolkitById(id: number): Promise<HitlToolkitModule | null> {
  const cached = moduleCache.get(id);
  if (cached) return cached;

  const promise = loadHitlToolkitByIdUncached(id).then(mod => {
    resolvedCache.set(id, mod);
    return mod;
  });
  moduleCache.set(id, promise);
  return promise;
}

async function loadHitlToolkitByIdUncached(id: number): Promise<HitlToolkitModule | null> {
  try {
    // Explicit imports per case for proper code splitting (template-literal
    // imports would bundle every toolkit together).
    switch (id) {
      case 1:
        return await import('@/components/assistant-ui/hitl/toolkits/t19-hitl');
      default:
        return null;
    }
  } catch {
    return null;
  }
}

/** Eagerly starts loading HITL toolkit chunks — call before the provider mounts. */
export function preloadHitlToolkits(ids: number[]): void {
  ids.forEach(id => loadHitlToolkitById(id));
}

function humanToolNamesOf(toolkit: HitlToolkitModule['toolkit']): Set<string> {
  const names = new Set<string>();
  for (const [name, entry] of Object.entries(toolkit ?? {})) {
    if ((entry as { type?: string } | undefined)?.type === 'human') names.add(name);
  }
  return names;
}

export function composeHitlModules(modules: HitlToolkitModule[]): HitlModule {
  if (modules.length === 0) return EMPTY_MODULE;

  const toolkit = Object.assign({}, ...modules.map(m => m.toolkit));

  const approvalUIs = modules.some(m => m.approvalUIs)
    ? Object.assign({}, ...modules.map(m => m.approvalUIs ?? {}))
    : undefined;

  const approvalTtlMs = modules.some(m => m.approvalTtlMs)
    ? Object.assign({}, ...modules.map(m => m.approvalTtlMs ?? {}))
    : undefined;

  const withTrail = modules.filter(m => m.getTrailConfig);
  const getTrailConfig: HitlGetTrailConfig | undefined = withTrail.length
    ? ctx => {
        for (const m of withTrail) {
          const steps = m.getTrailConfig!(ctx);
          if (steps) return steps;
        }
        return null;
      }
    : undefined;

  // Credential harvesting is looked up per tool name at request time; keep the
  // single-provider fast path (only one module today) but compose correctly.
  const credentialModules = modules.filter(m => m.getCredentials);
  const getCredentials = credentialModules.length
    ? async () =>
        Object.assign({}, ...(await Promise.all(credentialModules.map(m => m.getCredentials!()))))
    : undefined;

  const approvalToolNames = modules.flatMap(m => m.approvalToolNames ?? []);

  return {
    toolkit,
    approvalUIs,
    approvalTtlMs,
    getTrailConfig,
    getCredentials,
    approvalToolNames: approvalToolNames.length ? approvalToolNames : undefined,
    humanToolNames: humanToolNamesOf(toolkit),
  };
}

/** Tool names whose approval turns must carry harvested credentials. */
export function credentialToolNames(module: HitlModule): ReadonlySet<string> {
  if (!module.getCredentials) return new Set();
  return new Set(module.approvalToolNames ?? []);
}

function tryResolveSync(ids: number[]): HitlModule | null {
  if (ids.length === 0) return null;
  if (!ids.every(id => resolvedCache.has(id))) return null;
  const modules = ids
    .map(id => resolvedCache.get(id))
    .filter((m): m is HitlToolkitModule => m !== null);
  return modules.length > 0 ? composeHitlModules(modules) : null;
}

export function HitlProvider({ children }: { children: ReactNode }) {
  const hitlToolkitIds = useConfigValue('hitlToolkitIds') as number[] | undefined;
  const idsToLoad = useMemo(() => hitlToolkitIds ?? [], [hitlToolkitIds]);

  const [module, setModule] = useState<HitlModule | null>(() => tryResolveSync(idsToLoad));
  const idsKey = idsToLoad.join(',');

  const [prevIdsKey, setPrevIdsKey] = useState(idsKey);
  if (idsKey !== prevIdsKey) {
    setPrevIdsKey(idsKey);
    setModule(tryResolveSync(idsToLoad));
  }

  useEffect(() => {
    if (idsToLoad.length === 0) return;
    if (tryResolveSync(idsToLoad)) return;

    let cancelled = false;
    Promise.all(idsToLoad.map(id => loadHitlToolkitById(id))).then(results => {
      if (cancelled) return;
      const loaded = results.filter((m): m is HitlToolkitModule => m !== null);
      if (loaded.length > 0) setModule(composeHitlModules(loaded));
    });
    return () => {
      cancelled = true;
    };
  }, [idsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = useMemo(() => module ?? EMPTY_MODULE, [module]);

  return <HitlContext.Provider value={value}>{children}</HitlContext.Provider>;
}

export function useHitl(): HitlModule {
  return useContext(HitlContext);
}
